/**
 * La Coochie buy button (blocks/lc-buy-button.liquid).
 * Listens to `lc:selection` from the Color & size picker (same section) and sets the button's state, copy and
 * colour, announcing new copy through a polite live region. Submission, the /cart/add.js POST and cart events stay
 * in Horizon's product-form-component, which this block keeps around the form. Horizon's own in-flight guard
 * disables `ref="addToCartButton"`, which this block does not render, so this block guards the submit button's
 * click itself: one /cart/add per click until Horizon's `cart:lines-update` promise for this form settles (fallbacks:
 * `cart:error`, a safety timeout), and no submit at all in a non-purchasable state — the button is aria-disabled,
 * not disabled, so it stays in the Tab order and its copy ("Select your size") can be reached. If the section has
 * no picker at all, the button falls back to the variant Liquid rendered (selected / first available).
 */
import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

const ENABLED = new Set(['ready', 'low', 'final']);

const IN_FLIGHT_TIMEOUT = 8000;

class LcBuyButton extends Component {
  /** @type {Element | null} */ #section = null;
  #inFlight = false;
  /** Bumped on every release so a late promise from an earlier add can never release a newer one. */
  #generation = 0;
  /** @type {ReturnType<typeof setTimeout> | undefined} */ #inFlightTimer;

  connectedCallback() {
    super.connectedCallback();
    this.#section = this.closest('.shopify-section');
    this.#section?.addEventListener('lc:selection', this.#onSelection);
    this.addEventListener('click', this.#guardClick, true);
    this.addEventListener('click', this.#onWaitlistClick);
    this.addEventListener('submit', this.#arm);
    this.addEventListener(StandardEvents.cartLinesUpdate, this.#onLinesUpdate);
    this.addEventListener(StandardEvents.cartError, this.#release);
    if (!this.#section?.querySelector('lc-variant-picker')) this.#fallback();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#section?.removeEventListener('lc:selection', this.#onSelection);
    this.removeEventListener('click', this.#guardClick, true);
    this.removeEventListener('click', this.#onWaitlistClick);
    this.removeEventListener('submit', this.#arm);
    this.removeEventListener(StandardEvents.cartLinesUpdate, this.#onLinesUpdate);
    this.removeEventListener(StandardEvents.cartError, this.#release);
    clearTimeout(this.#inFlightTimer);
  }

  /**
   * Capture-phase guard on the submit button's click (implicit Enter submission also arrives as a synthetic click
   * on it). Horizon handles `submit` through a document-level capture listener that runs before anything on this
   * element, so the click is the only place a submit can be stopped. Stops a click while an add is in flight
   * (double-click, double-tap) and any click in a non-purchasable state (none / partial / soldout).
   * @param {Event} event
   */
  #guardClick = (event) => {
    const button = this.#button;
    if (!button || !(event.target instanceof Element) || event.target.closest('button') !== button) return;
    if (!this.#inFlight && ENABLED.has(button.dataset.state ?? '')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  };

  /**
   * Armed on `submit` (bubble phase — Horizon has accepted the submit; in its queued-add path, add clicked while a
   * variant fetch is pending, no POST starts yet) and again from #onLinesUpdate. Idempotent.
   */
  #arm = () => {
    this.#inFlight = true;
    this.#button?.setAttribute('aria-busy', 'true');
    clearTimeout(this.#inFlightTimer);
    this.#inFlightTimer = setTimeout(this.#release, IN_FLIGHT_TIMEOUT);
  };

  /**
   * Horizon dispatches `cart:lines-update` synchronously when the POST starts — in the direct path that is before
   * the `submit` event even reaches this element — and settles `event.promise` once the cart has been refreshed.
   * The guard is released on that settle, so the next click goes through as soon as the add is really done. The
   * event bubbles from <product-form-component>, so listening on this element scopes it to this form.
   * @param {Event & { promise?: Promise<unknown> }} event
   */
  #onLinesUpdate = (event) => {
    this.#arm();
    const generation = this.#generation;
    const done = () => {
      if (this.#generation === generation) this.#release();
    };
    if (event.promise instanceof Promise) event.promise.then(done, done);
    else done();
  };

  #release = () => {
    clearTimeout(this.#inFlightTimer);
    this.#inFlight = false;
    this.#generation += 1;
    this.#button?.removeAttribute('aria-busy');
  };

  /** No picker in the section: sell the fallback variant Liquid picked (selected_or_first_available_variant). */
  #fallback() {
    const { fallbackVariant, fallbackState } = this.dataset;
    if (!fallbackVariant || !fallbackState || !ENABLED.has(fallbackState)) return;
    const input = this.querySelector('input[name="id"]');
    if (input instanceof HTMLInputElement) input.value = fallbackVariant;
    this.#apply(fallbackState);
  }

  /** @param {Event} event */
  #onSelection = (event) => {
    const s = /** @type {CustomEvent} */ (event).detail;
    if (!s) return;
    const button = this.#button;
    if (!button) return;
    if (s.hex) this.style.setProperty('--lc-swatch', s.hex);
    button.dataset.mesh = String(Boolean(s.mesh));

    const finalSale = this.dataset.finalSale === 'true' || Boolean(s.final); // product in the sale collection, or the variant's flag
    let state = 'partial';
    if (s.full) {
      if (!s.variantId || !s.available) state = 'soldout';
      else if (s.low) state = 'low';
      else if (finalSale) state = 'final';
      else state = 'ready';
    }
    // the "Final Sale: Yes" line property travels with every final-sale add (low-stock ones too), never otherwise
    const property = this.querySelector('input[name="properties[Final Sale]"]');
    if (property instanceof HTMLInputElement) property.disabled = !finalSale;
    this.#announce(this.#apply(state));
    this.#publishVariant(s.full && s.variantId ? String(s.variantId) : '');
  };

  /**
   * Back-in-stock apps (Klaviyo today, Ordersify before it) read the `/cart/add` form's `[name="id"]` — some only after a
   * `change` event on the form. The picker's radios live in a sibling block, so their own change events never reach the
   * form, and Horizon's product-form only writes the id input once the section fetch resolves. Write the id now
   * (product-form writes the same value later) and let the form hear a change (2026-09-07 review). Horizon's own `on:change` handlers are delegated per element, so a change bubbling
   * from the hidden input reaches nothing else in the theme.
   * @param {string} variantId
   */
  #publishVariant(variantId) {
    const input = this.querySelector('input[name="id"]');
    if (!(input instanceof HTMLInputElement)) return;
    input.value = variantId;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * The button sits inside <product-form-component>, whose refs it belongs to (Horizon scopes refs to the nearest
   * component), so it is looked up by its ref attribute the way product-form.js does with its own.
   */
  get #button() {
    return /** @type {HTMLButtonElement | null} */ (this.querySelector('[ref="lcButton"]'));
  }

  /**
   * @param {string} state
   * @returns {string | undefined} the copy now on the button
   */
  #apply(state) {
    const button = this.#button;
    if (!button) return;
    const text = button.querySelector('[data-text]');
    const waitlist = /** @type {HTMLElement | null} */ (this.querySelector('[ref="waitlist"]'));
    const waitlistText = waitlist?.querySelector('[data-waitlist-text]');
    const copy = {
      partial: this.dataset.textSelect,
      ready: this.dataset.textAdd,
      low: this.dataset.textLow,
      soldout: this.dataset.textSoldout,
      final: this.dataset.textFinal,
    }[state];
    button.dataset.state = state;
    if (ENABLED.has(state)) button.removeAttribute('aria-disabled');
    else button.setAttribute('aria-disabled', 'true');
    if (text instanceof HTMLElement && copy != null) text.textContent = copy;
    // sold out: the Klaviyo waitlist link takes the button's slot (same classes, so the row keeps its 48 px box)
    button.hidden = state === 'soldout';
    if (waitlist) {
      waitlist.hidden = state !== 'soldout';
      if (waitlistText instanceof HTMLElement && copy != null && state === 'soldout') waitlistText.textContent = copy;
      const link = waitlist.querySelector('[ref="waitlistButton"]');
      if (link instanceof HTMLElement) link.dataset.mesh = button.dataset.mesh ?? 'false';
    }
    return copy;
  }

  /**
   * "Join the waitlist": forward the click to the hidden Klaviyo trigger. Klaviyo binds its Back in Stock modal to
   * `.klaviyo-bis-trigger` and toggles that element's own display, so the visible button stays ours (`on:click`).
   */
  openWaitlist() {
    // With a Back in Stock form published, Klaviyo injects its own button (#klaviyo-bis-button-container, hidden by the block's
    // CSS) and only that button opens the form (verified 2026-09-24: a click on the .klaviyo-bis-trigger anchor opens nothing).
    // Its React handler runs on a dispatched click, display: none or not.
    const injected = this.querySelector('#klaviyo-bis-button-container button');
    if (injected instanceof HTMLElement) return injected.click();
    const trigger = this.querySelector('[ref="waitlistTrigger"]');
    // a dispatched (untrusted) click reaches Klaviyo's listener but, unlike trigger.click(), never follows the anchor's "#"
    if (trigger instanceof HTMLElement) trigger.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
  }

  /** Plain listener, not `on:click`: Horizon resolves `on:` handlers on the nearest Component, which here is product-form-component. */
  #onWaitlistClick = (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('[ref="waitlistButton"]')) return;
    event.preventDefault();
    this.openWaitlist();
  };

  /**
   * Polite live region (ref="status", outside the form so Horizon's assertive error region is left alone): screen
   * readers hear "Add to bag" / "Low stock – Add to bag" / "Out of stock – Get notified" as the selection changes.
   * @param {string | undefined} copy
   */
  #announce(copy) {
    const { status } = this.refs;
    if (!(status instanceof HTMLElement) || copy == null || status.textContent === copy) return;
    status.textContent = copy;
  }
}

/** Debut-style quantity dropdown; product-form-component reads `refs.quantitySelector.getValue()` for queued adds. */
class LcQuantitySelect extends HTMLElement {
  getValue() {
    const select = this.querySelector('select');
    return select instanceof HTMLSelectElement ? select.value : '1';
  }
}

if (!customElements.get('lc-quantity-select')) {
  customElements.define('lc-quantity-select', LcQuantitySelect);
}

if (!customElements.get('lc-buy-button')) {
  customElements.define('lc-buy-button', LcBuyButton);
}
